import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getLabelDisplayName, PREDEFINED_LABELS } from "@/lib/recipe-categories";
import { X } from "lucide-react";
import { useState } from "react";
import type { FormEvent } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { RecipeFormValues } from "../_lib/recipeForm";

export type RecipeEditFormProps = Readonly<{
  form: UseFormReturn<RecipeFormValues>;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  boxLabels?: string[]; // Box-specific custom labels
}>;

export const RecipeEditForm = ({
  form,
  onSubmit,
  onCancel,
  isSubmitting,
  boxLabels = [],
}: RecipeEditFormProps) => {
  const [customLabelInput, setCustomLabelInput] = useState("");

  const allAvailableLabels = [...PREDEFINED_LABELS, ...boxLabels];

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., Classic Apple Pie" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Labels */}
          <FormField
            control={form.control}
            name="labels"
            render={({ field }) => {
              const currentLabels: string[] = field.value ?? [];

              const toggleLabel = (label: string) => {
                const next = currentLabels.includes(label)
                  ? currentLabels.filter((l) => l !== label)
                  : [...currentLabels, label];
                field.onChange(next);
              };

              const addCustomLabel = () => {
                const trimmed = customLabelInput.trim();
                if (trimmed && !currentLabels.includes(trimmed)) {
                  field.onChange([...currentLabels, trimmed]);
                }
                setCustomLabelInput("");
              };

              return (
                <FormItem>
                  <FormLabel>Labels</FormLabel>
                  <FormControl>
                    <div className="space-y-3">
                      {/* Predefined + box labels as toggleable chips */}
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                        {allAvailableLabels.map((label) => {
                          const selected = currentLabels.includes(label);
                          return (
                            <button
                              key={label}
                              type="button"
                              onClick={() => toggleLabel(label)}
                              className={`rounded-full px-2.5 py-1 text-xs transition-colors ${
                                selected
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground hover:bg-muted/80"
                              }`}
                            >
                              {getLabelDisplayName(label)}
                            </button>
                          );
                        })}
                      </div>
                      {/* Custom label input */}
                      <div className="flex gap-2">
                        <Input
                          placeholder="Add a custom label"
                          value={customLabelInput}
                          onChange={(e) => setCustomLabelInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addCustomLabel();
                            }
                          }}
                          className="h-8 text-sm"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addCustomLabel}
                          disabled={!customLabelInput.trim()}
                        >
                          Add
                        </Button>
                      </div>
                      {/* Selected labels */}
                      {currentLabels.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {currentLabels.map((label) => (
                            <Badge key={label} variant="secondary" className="gap-1 pr-1">
                              {label}
                              <button
                                type="button"
                                onClick={() => toggleLabel(label)}
                                className="ml-1 rounded-full hover:bg-destructive/20"
                                aria-label={`Remove ${label}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormDescription>
                    Select predefined labels or add your own to organize recipes.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <FormField
            control={form.control}
            name="prepTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Prep time</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., 20 minutes" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cookTime"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cook time</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., 45 minutes" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="servings"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Servings</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., 6 servings" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="ingredients"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Ingredients</FormLabel>
              <FormControl>
                <Textarea rows={10} placeholder="List each ingredient on a new line." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="directions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Directions</FormLabel>
              <FormControl>
                <Textarea rows={12} placeholder="Provide step-by-step instructions." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea rows={4} placeholder="Optional tips, variations, or serving suggestions." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="imageHint"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Image description</FormLabel>
              <FormControl>
                <Input placeholder="Short description for accessibility" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Draft and Sharing Settings */}
        <div className="rounded-lg border bg-card p-4">
          <h3 className="mb-4 font-semibold">Draft & Sharing Settings</h3>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="isDraft"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Save as draft</FormLabel>
                    <FormDescription>
                      Draft recipes are only visible to you. Uncheck to make this recipe visible to all recipe box members.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="isPublic"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value ?? false}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Make this recipe publicly shareable</FormLabel>
                    <FormDescription>
                      Allow others to view this recipe via a shareable link. You can optionally add a password for extra security.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            {form.watch("isPublic") && (
              <FormField
                control={form.control}
                name="sharePassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Share Password (Optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Leave empty for no password"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Require a password to view the shared recipe. Leave empty to allow anyone with the link to view.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>

        {/* Image editing is now handled via clicking on the image */}
        <div className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
          <p>💡 Tip: Click on the recipe image above to edit it.</p>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
